/**
 * PersonalLunaPanel.tsx
 * ─────────────────────────────────────────────────────────────────────────────
 * God Mode panel for managing Personal Luna app users.
 * Shows registered users, chat history, stats, and location data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { X, Users, MessageCircle, MapPin, Search, RefreshCw, Eye, Smartphone, Globe, TrendingUp, Activity, ChevronDown, ChevronRight } from 'lucide-react';

interface PersonalLunaUser {
  id: number;
  displayName: string;
  email: string | null;
  phone: string | null;
  preferredLanguage: string;
  status: string;
  lastLatitude: number | null;
  lastLongitude: number | null;
  lastLoginAt: string | null;
  createdAt: string;
  devicePlatform: string | null;
  appVersion: string | null;
  chatCount: number;
}

interface PersonalLunaStats {
  totalUsers: number;
  activeUsers: number;
  totalMessages: number;
  todayMessages: number;
}

interface ChatMessage {
  id: number;
  role: string;
  messageType: string;
  content: string;
  mediaUrl: string | null;
  latitude: number | null;
  longitude: number | null;
  sessionId: string;
  createdAt: string;
}

interface PersonalLunaPanelProps {
  isOpen: boolean;
  onClose: () => void;
  authHeader: () => Record<string, string>;
  isInline?: boolean;
}

const PersonalLunaPanel: React.FC<PersonalLunaPanelProps> = ({ isOpen, onClose, authHeader, isInline }) => {
  const [stats, setStats] = useState<PersonalLunaStats | null>(null);
  const [users, setUsers] = useState<PersonalLunaUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [userChats, setUserChats] = useState<ChatMessage[]>([]);
  const [chatLoading, setChatLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch('/api/admin/luna/stats', { headers: authHeader() });
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch {}
  }, [authHeader]);

  const fetchUsers = useCallback(async (p = 1, q = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), limit: '20' });
      if (q) params.set('search', q);
      const res = await fetch(`/api/admin/luna/users?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) {
        setUsers(data.users);
        setTotalPages(data.pagination.pages);
        setPage(p);
      }
    } catch {}
    setLoading(false);
  }, [authHeader]);

  const fetchUserChats = useCallback(async (userId: number) => {
    setChatLoading(true);
    try {
      const res = await fetch(`/api/admin/luna/users/${userId}/chats?limit=50`, { headers: authHeader() });
      const data = await res.json();
      if (data.success) setUserChats(data.messages);
    } catch {}
    setChatLoading(false);
  }, [authHeader]);

  useEffect(() => {
    if (isOpen || isInline) {
      fetchStats();
      fetchUsers();
    }
  }, [isOpen, isInline]);

  if (!isOpen && !isInline) return null;

  const content = (
    <div className={`bg-[#0a0e1a] border border-purple-500/20 rounded-2xl w-full flex flex-col overflow-hidden shadow-[0_0_60px_rgba(168,85,247,0.15)] ${isInline ? '' : 'max-w-6xl max-h-[90vh]'}`}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-purple-500/20 bg-black/40 shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center">
                <Smartphone className="text-purple-400" size={18} />
              </div>
              <div>
                <h2 className="text-base font-black text-white tracking-wide">PERSONAL LUNA</h2>
                <p className="text-[10px] text-purple-400/60 font-mono">Mobile app users — registration, chat, location</p>
              </div>
            </div>
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-purple-500/20 flex items-center justify-center text-purple-400/60 hover:text-white hover:border-purple-400 transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <StatCard icon={<Users size={16} />} label="Total Users" value={stats?.totalUsers || 0} color="text-purple-400" />
            <StatCard icon={<Activity size={16} />} label="Active (7d)" value={stats?.activeUsers || 0} color="text-emerald-400" />
            <StatCard icon={<MessageCircle size={16} />} label="Total Messages" value={stats?.totalMessages || 0} color="text-cyan-400" />
            <StatCard icon={<TrendingUp size={16} />} label="Today" value={stats?.todayMessages || 0} color="text-amber-400" />
          </div>

          {/* Search + Refresh */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={14} />
              <input
                type="text"
                placeholder="Search users by name, email, or phone..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchUsers(1, search); }}
                className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-300 outline-none focus:border-purple-500/50 font-mono"
              />
            </div>
            <button onClick={() => fetchUsers(1, search)}
              className="px-3 py-2 rounded-lg bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-bold hover:bg-purple-500/30 transition-colors flex items-center gap-1.5">
              <RefreshCw size={12} /> Refresh
            </button>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto rounded-xl border border-white/10 mb-4">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-white/10 bg-black/30">
                  {['User', 'Contact', 'Language', 'Platform', 'Location', 'Chats', 'Last Active', 'Actions'].map(h => (
                    <th key={h} className="px-3 py-2 text-left text-[9px] font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Loading...</td></tr>
                ) : users.length === 0 ? (
                  <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">No users found</td></tr>
                ) : users.map(u => (
                  <tr key={u.id} className="hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => { setSelectedUser(u.id === selectedUser ? null : u.id); if (u.id !== selectedUser) fetchUserChats(u.id); }}
                  >
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-300 text-xs font-bold">
                          {u.displayName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-white font-medium text-xs">{u.displayName}</p>
                          <p className="text-[9px] text-slate-500 font-mono">ID: {u.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {u.email && <p className="text-cyan-300 text-[10px]">{u.email}</p>}
                      {u.phone && <p className="text-green-300 text-[10px]">{u.phone}</p>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${u.preferredLanguage === 'si' ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700/50 text-slate-400'}`}>
                        {u.preferredLanguage === 'si' ? 'සිංහල' : 'English'}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="text-[10px] text-slate-400">{u.devicePlatform || '—'}</span>
                    </td>
                    <td className="px-3 py-2">
                      {u.lastLatitude && u.lastLongitude ? (
                        <a href={`https://www.google.com/maps?q=${u.lastLatitude},${u.lastLongitude}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300 flex items-center gap-1 text-[10px]"
                          onClick={e => e.stopPropagation()}
                        >
                          <MapPin size={10} /> {Number(u.lastLatitude).toFixed(3)}, {Number(u.lastLongitude).toFixed(3)}
                        </a>
                      ) : (
                        <span className="text-slate-600 text-[10px]">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-purple-300">{u.chatCount}</td>
                    <td className="px-3 py-2 text-[10px] text-slate-400 font-mono whitespace-nowrap">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </td>
                    <td className="px-3 py-2">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedUser(u.id); fetchUserChats(u.id); }}
                        className="p-1.5 rounded-md bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/20 transition-colors">
                        <Eye size={12} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mb-4">
              <button onClick={() => fetchUsers(page - 1, search)} disabled={page <= 1}
                className="px-3 py-1 rounded bg-white/5 text-xs text-slate-400 hover:bg-white/10 disabled:opacity-30">Prev</button>
              <span className="text-xs text-slate-500 font-mono">Page {page} of {totalPages}</span>
              <button onClick={() => fetchUsers(page + 1, search)} disabled={page >= totalPages}
                className="px-3 py-1 rounded bg-white/5 text-xs text-slate-400 hover:bg-white/10 disabled:opacity-30">Next</button>
            </div>
          )}

          {/* Chat History Panel */}
          {selectedUser && (
            <div className="border border-purple-500/20 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-purple-500/5 border-b border-purple-500/20 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MessageCircle size={14} className="text-purple-400" />
                  <span className="text-xs font-bold text-purple-300">
                    Chat History — {users.find(u => u.id === selectedUser)?.displayName}
                  </span>
                </div>
                <button onClick={() => { setSelectedUser(null); setUserChats([]); }}
                  className="text-[10px] text-slate-500 hover:text-slate-300 underline">Close</button>
              </div>
              <div className="max-h-[300px] overflow-y-auto p-3 space-y-2">
                {chatLoading ? (
                  <p className="text-center text-slate-500 text-xs py-4">Loading chats...</p>
                ) : userChats.length === 0 ? (
                  <p className="text-center text-slate-500 text-xs py-4">No messages yet</p>
                ) : userChats.map(msg => (
                  <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] rounded-xl px-3 py-2 text-xs ${
                      msg.role === 'user'
                        ? 'bg-purple-500/20 border border-purple-500/30 text-purple-100'
                        : 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-100'
                    }`}>
                      <p className="text-[9px] text-slate-500 mb-1 font-mono">
                        {msg.role === 'user' ? '👤' : '🤖'} {new Date(msg.createdAt).toLocaleString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        {msg.messageType !== 'text' && <span className="ml-1 text-purple-400">[{msg.messageType}]</span>}
                      </p>
                      <p>{msg.content}</p>
                      {msg.latitude && msg.longitude && (
                        <a href={`https://www.google.com/maps?q=${msg.latitude},${msg.longitude}`}
                          target="_blank" rel="noopener noreferrer"
                          className="text-purple-400 hover:text-purple-300 text-[10px] flex items-center gap-1 mt-1">
                          <MapPin size={10} /> View location
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    if (isInline) return content;

    return (
      <div className="fixed inset-0 z-[75] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      >
        {content}
      </div>
    );
  };

const StatCard = ({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number; color: string }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-3">
    <div className="flex items-center gap-2 mb-1">
      <span className={color}>{icon}</span>
      <span className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">{label}</span>
    </div>
    <p className={`text-lg font-black ${color}`}>{value.toLocaleString()}</p>
  </div>
);

export default PersonalLunaPanel;
